import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCapitalInvestments,
  useInvestmentContracts,
  useInvestmentPayments,
  useAddInvestmentContract,
  useUpdateInvestmentContract,
  useDeleteInvestmentContract,
  useAddInvestmentPayment,
  useUpdateInvestmentPayment,
  useDeleteInvestmentPayment,
} from '../hooks/useCapitalInvestments';
import { formatCurrency } from '../utils/format';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  Construction as ConstructionIcon,
} from '@mui/icons-material';

// ── Shared constants ─────────────────────────────────────────────────────────

const PAYMENT_CATEGORIES = [
  'Materials',
  'Labour',
  'Equipment',
  'Permits & Fees',
  'Professional Fees',
  'Other',
] as const;

const CATEGORY_COLORS: Record<string, string> = {
  Materials: '#ff9800',
  Labour: '#42a5f5',
  Equipment: '#ab47bc',
  'Permits & Fees': '#26c6da',
  'Professional Fees': '#66bb6a',
  Other: '#78909c',
};

const EMPTY_PAYMENT = {
  description: '',
  category: 'Other' as string,
  contract_ids: [] as number[],
  amount_ghs: '',
  payment_date: '',
  notes: '',
};

const EMPTY_CONTRACT = { name: '', category: 'Labour', contractor_name: '', agreed_amount_ghs: '' };

const fmtDate = (d: string) =>
  d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

// ── Shared sx helpers ─────────────────────────────────────────────────────────

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#0d0d0d',
    color: '#e0e0e0',
    borderRadius: 1.5,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(66,165,245,0.35)' },
    '&.Mui-focused fieldset': { borderColor: '#42a5f5' },
  },
  '& .MuiSelect-icon': { color: '#555' },
};

const labelSx = {
  color: '#666',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.5px',
  mb: 0.5,
  display: 'block',
  textTransform: 'uppercase' as const,
};

// ── KPI tile ─────────────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  color = '#e0e0e0',
  sub,
}: {
  label: string;
  value: string;
  color?: string;
  sub?: string;
}) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        minWidth: 130,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 700,
          color: '#555',
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '1.15rem',
          fontWeight: 800,
          color,
          lineHeight: 1.15,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
      {sub && <Typography sx={{ fontSize: '0.65rem', color: '#555', mt: 0.25 }}>{sub}</Typography>}
    </Box>
  );
}

// ── Category dot + label ──────────────────────────────────────────────────────

function CatDot({ cat, size = 7 }: { cat: string; size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: CATEGORY_COLORS[cat] ?? '#78909c',
        flexShrink: 0,
      }}
    />
  );
}

// ── Contract form (hoisted to avoid remount-on-rerender focus loss) ───────────

type ContractFormValues = typeof EMPTY_CONTRACT;

function ContractForm({
  form,
  setForm,
  editId,
  onSave,
  onCancel,
  saving,
  noBorder,
}: {
  form: ContractFormValues;
  setForm: React.Dispatch<React.SetStateAction<ContractFormValues>>;
  editId: number | null;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  noBorder?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        p: noBorder ? 0 : 2,
        borderRadius: noBorder ? 0 : 2,
        border: noBorder ? 'none' : '1px solid rgba(66,165,245,0.2)',
        bgcolor: noBorder ? 'transparent' : 'rgba(66,165,245,0.03)',
      }}
    >
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 7 }}>
          <Typography component="span" sx={labelSx}>
            Work description *
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={form.name}
            placeholder="e.g. Masonry — Block A"
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            sx={inputSx}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 5 }}>
          <Typography component="span" sx={labelSx}>
            Category *
          </Typography>
          <Select
            fullWidth
            size="small"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            sx={{
              ...inputSx,
              '& .MuiSelect-select': { color: CATEGORY_COLORS[form.category] ?? '#e0e0e0' },
            }}
          >
            {PAYMENT_CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat} sx={{ fontSize: '0.82rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CatDot cat={cat} />
                  {cat}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography component="span" sx={labelSx}>
            Contractor
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={form.contractor_name}
            placeholder="Optional"
            onChange={(e) => setForm((f) => ({ ...f, contractor_name: e.target.value }))}
            sx={inputSx}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography component="span" sx={labelSx}>
            Agreed amount (GH₵) *
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            slotProps={{
              htmlInput: { min: 0.01, step: 0.01 },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#555', fontSize: '0.8rem' }}>GH₵</Typography>
                  </InputAdornment>
                ),
              },
            }}
            value={form.agreed_amount_ghs}
            onChange={(e) => setForm((f) => ({ ...f, agreed_amount_ghs: e.target.value }))}
            sx={inputSx}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button size="small" onClick={onCancel} sx={{ color: '#666', fontSize: '0.75rem' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={saving || !form.name || !form.agreed_amount_ghs}
          onClick={onSave}
          sx={{
            background: 'linear-gradient(135deg, #42a5f5, #1976d2)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.75rem',
          }}
        >
          {saving ? 'Saving…' : editId ? 'Save Changes' : 'Add Contract'}
        </Button>
      </Box>
    </Box>
  );
}

// ── Contracts panel ───────────────────────────────────────────────────────────

function ContractsPanel({ investmentId }: { investmentId: number }) {
  const { data: contracts = [], isLoading } = useInvestmentContracts(investmentId);
  const addMut = useAddInvestmentContract();
  const updateMut = useUpdateInvestmentContract();
  const delMut = useDeleteInvestmentContract();

  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_CONTRACT);

  const totalAgreed = (contracts as any[]).reduce(
    (s, c) => s + parseFloat(c.agreed_amount_ghs ?? 0),
    0
  );
  const totalPaid = (contracts as any[]).reduce((s, c) => s + parseFloat(c.total_paid_ghs ?? 0), 0);

  const openEdit = (c: any) => {
    setForm({
      name: c.name,
      category: c.category,
      contractor_name: c.contractor_name ?? '',
      agreed_amount_ghs: String(c.agreed_amount_ghs),
    });
    setEditId(c.id);
    setShowAdd(false);
  };

  const reset = () => {
    setForm(EMPTY_CONTRACT);
    setEditId(null);
    setShowAdd(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', minHeight: 0 }}>
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: '#555',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
            }}
          >
            Work Contracts
          </Typography>
          {(contracts as any[]).length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    color: '#444',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                  }}
                >
                  Paid
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 800,
                    color: '#42a5f5',
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.2,
                  }}
                >
                  {formatCurrency(totalPaid)}
                </Typography>
              </Box>
              <Box sx={{ width: 1, bgcolor: 'rgba(255,255,255,0.06)', alignSelf: 'stretch' }} />
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    color: '#444',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    lineHeight: 1,
                  }}
                >
                  Agreed
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 800,
                    color: '#999',
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.2,
                  }}
                >
                  {formatCurrency(totalAgreed)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
          onClick={() => {
            setShowAdd((v) => !v);
            setEditId(null);
            setForm(EMPTY_CONTRACT);
          }}
          sx={{
            fontSize: '0.72rem',
            color: '#42a5f5',
            minWidth: 0,
            px: 1.5,
            bgcolor: 'rgba(66,165,245,0.08)',
            borderRadius: 1.5,
            '&:hover': { bgcolor: 'rgba(66,165,245,0.14)' },
          }}
        >
          {showAdd ? 'Cancel' : 'Add'}
        </Button>
      </Box>

      {showAdd && (
        <ContractForm
          form={form}
          setForm={setForm}
          editId={editId}
          onCancel={reset}
          saving={addMut.isPending}
          onSave={() =>
            addMut.mutate(
              {
                id: investmentId,
                data: {
                  ...form,
                  agreed_amount_ghs: parseFloat(form.agreed_amount_ghs),
                  contractor_name: form.contractor_name || null,
                },
              },
              { onSuccess: reset }
            )
          }
        />
      )}

      {isLoading ? (
        <LinearProgress sx={{ borderRadius: 1 }} />
      ) : (contracts as any[]).length === 0 && !showAdd ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <ConstructionIcon sx={{ fontSize: 28, color: '#333', mb: 1 }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#444' }}>No contracts yet</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#333', mt: 0.5 }}>
            Add one to track agreed amounts per scope of work
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflowY: 'auto',
          }}
        >
          {[...(contracts as any[])]
            .sort((a, b) => {
              const aRemaining = parseFloat(
                a.remaining_ghs ??
                  parseFloat(a.agreed_amount_ghs) - parseFloat(a.total_paid_ghs ?? 0)
              );
              const bRemaining = parseFloat(
                b.remaining_ghs ??
                  parseFloat(b.agreed_amount_ghs) - parseFloat(b.total_paid_ghs ?? 0)
              );
              const aFull = aRemaining <= 0;
              const bFull = bRemaining <= 0;
              if (aFull !== bFull) return Number(aFull) - Number(bFull);
              return aRemaining - bRemaining;
            })
            .map((c: any) => {
              const paid = parseFloat(c.total_paid_ghs ?? 0);
              const agreed = parseFloat(c.agreed_amount_ghs);
              const remaining = parseFloat(c.remaining_ghs ?? agreed - paid);
              const pct = agreed > 0 ? Math.min((paid / agreed) * 100, 100) : 0;
              const color = CATEGORY_COLORS[c.category] ?? '#78909c';
              const overrun = paid > agreed;
              const isEditing = editId === c.id;

              return (
                <Box
                  key={c.id}
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${isEditing ? '#42a5f5' : color + '22'}`,
                    bgcolor: isEditing ? 'rgba(66,165,245,0.03)' : `${color}05`,
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {/* Color bar */}
                  <Box sx={{ height: 3, bgcolor: color, opacity: 0.6 }} />

                  <Box sx={{ p: 1.75 }}>
                    {isEditing ? (
                      <ContractForm
                        noBorder
                        form={form}
                        setForm={setForm}
                        editId={editId}
                        onCancel={reset}
                        saving={updateMut.isPending}
                        onSave={() =>
                          updateMut.mutate(
                            {
                              investmentId,
                              contractId: c.id,
                              data: {
                                ...form,
                                agreed_amount_ghs: parseFloat(form.agreed_amount_ghs),
                                contractor_name: form.contractor_name || null,
                              },
                            },
                            { onSuccess: reset }
                          )
                        }
                      />
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            mb: 1,
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: '#e0e0e0',
                                fontSize: '0.88rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {c.name}
                            </Typography>
                            {c.contractor_name && (
                              <Typography sx={{ color: '#555', fontSize: '0.72rem' }}>
                                {c.contractor_name}
                              </Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-end',
                              gap: 0.5,
                              ml: 1,
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.95rem',
                                fontWeight: 800,
                                color: '#e0e0e0',
                                fontVariantNumeric: 'tabular-nums',
                                lineHeight: 1,
                              }}
                            >
                              {formatCurrency(agreed)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip
                                label={c.category}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  bgcolor: `${color}15`,
                                  color,
                                  border: `1px solid ${color}30`,
                                }}
                              />
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => openEdit(c)}
                                  sx={{
                                    color: '#42a5f5',
                                    '&:hover': { bgcolor: 'rgba(66,165,245,0.1)' },
                                    p: 0.3,
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: '0.82rem' }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  onClick={() => delMut.mutate({ investmentId, contractId: c.id })}
                                  sx={{
                                    color: '#f44336',
                                    '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' },
                                    p: 0.3,
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: '0.82rem' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>

                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{
                            height: 5,
                            borderRadius: 3,
                            mb: 1,
                            bgcolor: 'rgba(255,255,255,0.05)',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: overrun ? '#f44336' : color,
                              borderRadius: 3,
                            },
                          }}
                        />

                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: '#888',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            <span style={{ color: '#ddd', fontWeight: 700 }}>
                              {formatCurrency(paid)}
                            </span>{' '}
                            paid
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.72rem',
                              color: overrun ? '#f44336' : '#66bb6a',
                              fontWeight: 700,
                            }}
                          >
                            {overrun
                              ? `+${formatCurrency(paid - agreed)} over`
                              : `${formatCurrency(remaining)} left`}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
      )}
    </Box>
  );
}

// ── Payments panel ────────────────────────────────────────────────────────────

function PaymentsPanel({ investmentId }: { investmentId: number }) {
  const { data: contracts = [] } = useInvestmentContracts(investmentId);
  const { data: payments = [], isLoading } = useInvestmentPayments(investmentId);
  const addMut = useAddInvestmentPayment();
  const updateMut = useUpdateInvestmentPayment();
  const delMut = useDeleteInvestmentPayment();

  const [form, setForm] = useState(EMPTY_PAYMENT);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [contractFilter, setContractFilter] = useState<number | null>(null);
  const set = (f: string, v: any) => setForm((p) => ({ ...p, [f]: v }));

  const paymentList = payments as any[];

  const filteredPayments = useMemo(() => {
    let list = paymentList;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) => p.description?.toLowerCase().includes(q));
    }
    if (contractFilter !== null) {
      list = list.filter((p) => (p.contracts ?? []).some((c: any) => c.id === contractFilter));
    }
    return list;
  }, [paymentList, search, contractFilter]);

  const total = filteredPayments.reduce((s, p) => s + parseFloat(p.amount_ghs), 0);

  const byCategory = useMemo(() => {
    const map: Record<string, { total: number; items: any[] }> = {};
    for (const p of filteredPayments) {
      const cat = p.category ?? 'Other';
      if (!map[cat]) map[cat] = { total: 0, items: [] };
      map[cat].total += parseFloat(p.amount_ghs);
      map[cat].items.push(p);
    }
    return map;
  }, [filteredPayments]);

  const canAdd = !!form.description && !!form.amount_ghs && !!form.payment_date;

  const handleAdd = () => {
    addMut.mutate(
      {
        id: investmentId,
        data: {
          description: form.description,
          category: form.category,
          contract_ids: form.contract_ids,
          amount_ghs: parseFloat(form.amount_ghs as string),
          payment_date: form.payment_date,
          notes: form.notes || null,
        },
      },
      {
        onSuccess: () => {
          setForm(EMPTY_PAYMENT);
          setErr('');
        },
        onError: () => setErr('Failed to add payment.'),
      }
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: 0 }}>
      {/* ── History ── */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
        {/* Filter bar */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: '#555',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              mr: 'auto',
            }}
          >
            Payment History
          </Typography>
          <TextField
            size="small"
            placeholder="Search payments…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ color: '#444', fontSize: '0.8rem' }}>🔍</Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              width: 180,
              '& .MuiOutlinedInput-root': {
                bgcolor: '#0d0d0d',
                color: '#e0e0e0',
                borderRadius: 1.5,
                fontSize: '0.8rem',
                height: 30,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(66,165,245,0.35)' },
                '&.Mui-focused fieldset': { borderColor: '#42a5f5' },
              },
            }}
          />
          {(contracts as any[]).length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip
                label="All"
                size="small"
                onClick={() => setContractFilter(null)}
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  bgcolor:
                    contractFilter === null ? 'rgba(66,165,245,0.18)' : 'rgba(255,255,255,0.05)',
                  color: contractFilter === null ? '#42a5f5' : '#666',
                  border:
                    contractFilter === null
                      ? '1px solid rgba(66,165,245,0.4)'
                      : '1px solid transparent',
                  '&:hover': { bgcolor: 'rgba(66,165,245,0.12)' },
                }}
              />
              {(contracts as any[]).map((c: any) => {
                const active = contractFilter === c.id;
                const color = CATEGORY_COLORS[c.category] ?? '#78909c';
                return (
                  <Chip
                    key={c.id}
                    label={c.name}
                    size="small"
                    onClick={() => setContractFilter(active ? null : c.id)}
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      maxWidth: 130,
                      cursor: 'pointer',
                      bgcolor: active ? `${color}20` : 'rgba(255,255,255,0.05)',
                      color: active ? color : '#555',
                      border: active ? `1px solid ${color}50` : '1px solid transparent',
                      '&:hover': { bgcolor: `${color}14` },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>

        {isLoading ? (
          <LinearProgress sx={{ borderRadius: 1 }} />
        ) : filteredPayments.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <PaymentIcon sx={{ fontSize: 28, color: '#333', mb: 1 }} />
            <Typography sx={{ fontSize: '0.8rem', color: '#444' }}>
              {paymentList.length === 0
                ? 'No payments recorded yet'
                : 'No payments match the filter'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Category breakdown stacked bar */}
            {total > 0 && (
              <Box>
                <Box
                  sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', mb: 1 }}
                >
                  {Object.entries(byCategory).map(([cat, { total: catTotal }]) => (
                    <Tooltip key={cat} title={`${cat}: ${formatCurrency(catTotal)}`}>
                      <Box
                        sx={{
                          width: `${(catTotal / total) * 100}%`,
                          bgcolor: CATEGORY_COLORS[cat] ?? '#78909c',
                          transition: 'width 0.3s',
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {Object.entries(byCategory).map(([cat, { total: catTotal }]) => {
                    const color = CATEGORY_COLORS[cat] ?? '#78909c';
                    return (
                      <Box key={cat} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <CatDot cat={cat} size={7} />
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.62rem',
                              color: '#555',
                              fontWeight: 700,
                              letterSpacing: '0.4px',
                              textTransform: 'uppercase',
                              lineHeight: 1,
                            }}
                          >
                            {cat}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.95rem',
                              fontWeight: 800,
                              color,
                              fontVariantNumeric: 'tabular-nums',
                              lineHeight: 1.2,
                            }}
                          >
                            {formatCurrency(catTotal)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: '#555', lineHeight: 1 }}>
                            {((catTotal / total) * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Payments grouped by category */}
            {Object.entries(byCategory).map(([cat, { total: catTotal, items }]) => {
              const color = CATEGORY_COLORS[cat] ?? '#78909c';
              return (
                <Box key={cat}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ width: 3, height: 14, borderRadius: 2, bgcolor: color }} />
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                      }}
                    >
                      {cat}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        color: '#444',
                        ml: 'auto',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatCurrency(catTotal)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 1.5 }}>
                    {items.map((p: any) => (
                      <Box
                        key={p.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: `${color}07`,
                          border: `1px solid ${color}15`,
                          '&:hover': { bgcolor: `${color}10` },
                          transition: 'background 0.15s',
                        }}
                      >
                        <Box flex={1} minWidth={0}>
                          <Typography
                            sx={{
                              color: '#d0d0d0',
                              fontWeight: 600,
                              fontSize: '0.84rem',
                              lineHeight: 1.3,
                            }}
                            noWrap
                          >
                            {p.description}
                          </Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: '#555' }}>
                            {fmtDate(p.payment_date)}
                            {p.notes ? ` · ${p.notes}` : ''}
                          </Typography>
                        </Box>

                        {/* Inline category reassign */}
                        <Select
                          size="small"
                          value={p.category ?? 'Other'}
                          onChange={(e) =>
                            updateMut.mutate({
                              investmentId,
                              paymentId: p.id,
                              data: { category: e.target.value },
                            })
                          }
                          sx={{
                            fontSize: '0.7rem',
                            height: 26,
                            flexShrink: 0,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: `${CATEGORY_COLORS[p.category ?? 'Other']}35`,
                            },
                            '& .MuiSelect-select': {
                              py: 0.4,
                              px: 0.75,
                              color: CATEGORY_COLORS[p.category ?? 'Other'] ?? '#78909c',
                            },
                            '& .MuiSelect-icon': { color: '#444', fontSize: '1rem' },
                          }}
                          renderValue={(val) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CatDot cat={val as string} size={6} />
                              {val}
                            </Box>
                          )}
                        >
                          {PAYMENT_CATEGORIES.map((c) => (
                            <MenuItem key={c} value={c} sx={{ fontSize: '0.8rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CatDot cat={c} />
                                {c}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>

                        {/* Inline contract link */}
                        {(contracts as any[]).length > 0 && (
                          <Select
                            size="small"
                            multiple
                            displayEmpty
                            value={(p.contracts ?? []).map((c: any) => c.id)}
                            onChange={(e) =>
                              updateMut.mutate({
                                investmentId,
                                paymentId: p.id,
                                data: { contract_ids: e.target.value as number[] },
                              })
                            }
                            sx={{
                              fontSize: '0.7rem',
                              height: 26,
                              flexShrink: 0,
                              minWidth: 90,
                              maxWidth: 140,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor:
                                  (p.contracts ?? []).length > 0
                                    ? 'rgba(66,165,245,0.4)'
                                    : 'rgba(255,255,255,0.07)',
                              },
                              '& .MuiSelect-select': { py: 0.4, px: 0.75, fontSize: '0.7rem' },
                              '& .MuiSelect-icon': { color: '#444', fontSize: '1rem' },
                            }}
                            renderValue={(sel) => {
                              const ids = sel as number[];
                              if (ids.length === 0)
                                return (
                                  <Typography sx={{ fontSize: '0.7rem', color: '#444' }}>
                                    Contracts
                                  </Typography>
                                );
                              return (
                                <Typography sx={{ fontSize: '0.7rem', color: '#42a5f5' }} noWrap>
                                  {ids.length === 1
                                    ? ((contracts as any[]).find((c: any) => c.id === ids[0])
                                        ?.name ?? 'Contract')
                                    : `${ids.length} contracts`}
                                </Typography>
                              );
                            }}
                          >
                            {(contracts as any[]).map((c: any) => (
                              <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.8rem' }}>
                                <Checkbox
                                  checked={(p.contracts ?? []).some((x: any) => x.id === c.id)}
                                  size="small"
                                  sx={{ p: 0.5 }}
                                />
                                {c.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}

                        <Typography
                          sx={{
                            color,
                            fontWeight: 800,
                            fontSize: '0.88rem',
                            flexShrink: 0,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {formatCurrency(parseFloat(p.amount_ghs))}
                        </Typography>

                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => delMut.mutate({ investmentId, paymentId: p.id })}
                            disabled={delMut.isPending}
                            sx={{
                              color: '#f44336',
                              '&:hover': { bgcolor: 'rgba(244,67,54,0.1)' },
                              p: 0.25,
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}

            {/* Grand total */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                py: 1.25,
                bgcolor: 'rgba(66,165,245,0.07)',
                borderRadius: 1.5,
                border: '1px solid rgba(66,165,245,0.18)',
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', color: '#888', fontWeight: 600 }}>
                Total paid so far
              </Typography>
              <Typography
                sx={{
                  color: '#42a5f5',
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatCurrency(total)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Add payment form (pinned) ── */}
      <Box sx={{ flexShrink: 0, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 700,
            color: '#555',
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            mb: 1.5,
          }}
        >
          Record Payment
        </Typography>

        {err && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.25, fontSize: '0.8rem' }}>
            {err}
          </Alert>
        )}

        {(contracts as any[]).length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography component="span" sx={labelSx}>
              Link to contracts (optional)
            </Typography>
            <Select
              fullWidth
              size="small"
              multiple
              displayEmpty
              value={form.contract_ids}
              onChange={(e) => set('contract_ids', e.target.value)}
              sx={inputSx}
              renderValue={(sel) => {
                const ids = sel as number[];
                if (ids.length === 0)
                  return (
                    <Typography sx={{ fontSize: '0.82rem', color: '#555' }}>
                      None selected
                    </Typography>
                  );
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {ids.map((id) => {
                      const c = (contracts as any[]).find((x: any) => x.id === id);
                      return (
                        <Chip
                          key={id}
                          label={c?.name ?? id}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            bgcolor: `${CATEGORY_COLORS[c?.category] ?? '#78909c'}20`,
                            color: CATEGORY_COLORS[c?.category] ?? '#aaa',
                          }}
                        />
                      );
                    })}
                  </Box>
                );
              }}
            >
              {(contracts as any[]).map((c: any) => (
                <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.82rem' }}>
                  <Checkbox
                    checked={form.contract_ids.includes(c.id)}
                    size="small"
                    sx={{ p: 0.5, color: CATEGORY_COLORS[c.category] ?? '#aaa' }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5 }}>
                    <CatDot cat={c.category} />
                    <span>{c.name}</span>
                    <Typography component="span" sx={{ color: '#555', fontSize: '0.75rem' }}>
                      ({formatCurrency(parseFloat(c.remaining_ghs ?? 0))} left)
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <Typography component="span" sx={labelSx}>
              Description *
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={form.description}
              placeholder="e.g. Concrete supply — block A"
              onChange={(e) => set('description', e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography component="span" sx={labelSx}>
              Category
            </Typography>
            <Select
              fullWidth
              size="small"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              sx={inputSx}
              renderValue={(val) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CatDot cat={val as string} />
                  <Typography sx={{ fontSize: '0.82rem', color: '#e0e0e0' }}>{val}</Typography>
                </Box>
              )}
            >
              {PAYMENT_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CatDot cat={cat} />
                    {cat}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={labelSx}>
              Amount (GH₵) *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              slotProps={{
                htmlInput: { min: 0.01, step: 0.01 },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: '#555', fontSize: '0.8rem' }}>GH₵</Typography>
                    </InputAdornment>
                  ),
                },
              }}
              value={form.amount_ghs}
              onChange={(e) => set('amount_ghs', e.target.value)}
              sx={inputSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography component="span" sx={labelSx}>
              Date *
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              value={form.payment_date}
              onChange={(e) => set('payment_date', e.target.value)}
              sx={inputSx}
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          size="small"
          label="Notes (optional)"
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
          sx={{ ...inputSx, mb: 2 }}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleAdd}
          disabled={addMut.isPending || !canAdd}
          sx={{
            background: 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)',
            color: '#fff',
            fontWeight: 700,
            py: 1.1,
            borderRadius: 1.5,
            '&:hover': { background: 'linear-gradient(135deg, #64b5f6, #42a5f5)' },
            '&.Mui-disabled': { background: 'rgba(66,165,245,0.15)', color: '#555' },
          }}
        >
          {addMut.isPending ? 'Recording…' : 'Record Payment'}
        </Button>
      </Box>
    </Box>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    active: { label: 'Active', color: '#42a5f5', bg: 'rgba(66,165,245,0.12)' },
    completed: { label: 'Completed', color: '#66bb6a', bg: 'rgba(102,187,106,0.12)' },
    on_hold: { label: 'On Hold', color: '#ff9800', bg: 'rgba(255,152,0,0.12)' },
    cancelled: { label: 'Cancelled', color: '#f44336', bg: 'rgba(244,67,54,0.12)' },
  };
  const s = map[status] ?? { label: status, color: '#888', bg: 'rgba(255,255,255,0.06)' };
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.35,
        borderRadius: 1,
        bgcolor: s.bg,
        border: `1px solid ${s.color}30`,
        display: 'inline-flex',
      }}
    >
      <Typography
        sx={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, letterSpacing: '0.4px' }}
      >
        {s.label}
      </Typography>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CapitalInvestmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: investments = [] } = useCapitalInvestments();

  const investment = (investments as any[]).find((i) => i.id === Number(id));

  if (!investment) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#121212',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: '#555', mb: 2 }}>Investment not found</Typography>
          <Button
            onClick={() => navigate('/capital-investments')}
            startIcon={<BackIcon />}
            sx={{ color: '#42a5f5' }}
          >
            Back
          </Button>
        </Box>
      </Box>
    );
  }

  const budget = parseFloat(investment.cost_ghs ?? 0);
  const paid = parseFloat(investment.total_paid_ghs ?? 0);
  const remaining = budget - paid;
  const pct = budget > 0 ? Math.min((paid / budget) * 100, 100) : 0;
  const overrun = paid > budget;

  return (
    <Box
      sx={{
        height: '100vh',
        bgcolor: '#121212',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* ── Sticky header ── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: '#121212',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <Box sx={{ px: { xs: 2, md: 4 }, py: 2 }}>
          {/* Top row: back + title */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <IconButton
              onClick={() => navigate('/capital-investments')}
              sx={{
                color: '#666',
                '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                mt: 0.25,
                flexShrink: 0,
              }}
            >
              <BackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.1rem', md: '1.35rem' },
                    color: '#f5f5f5',
                    lineHeight: 1.2,
                  }}
                >
                  {investment.name}
                </Typography>
                <StatusBadge status={investment.status ?? 'active'} />
              </Box>
              {investment.notes && (
                <Typography sx={{ fontSize: '0.78rem', color: '#555', mt: 0.4 }} noWrap>
                  {investment.notes}
                </Typography>
              )}
            </Box>
          </Box>

          {/* KPI strip + progress */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              overflowX: 'auto',
              pb: 0.5,
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <KpiTile label="Budget" value={formatCurrency(budget)} />
            <KpiTile
              label="Paid"
              value={formatCurrency(paid)}
              color="#42a5f5"
              sub={`${pct.toFixed(1)}% of budget`}
            />
            <KpiTile
              label={overrun ? 'Overrun' : 'Remaining'}
              value={formatCurrency(Math.abs(remaining))}
              color={overrun ? '#f44336' : remaining === 0 ? '#66bb6a' : '#e0e0e0'}
            />
          </Box>

          {/* Budget progress bar */}
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.05)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: overrun ? '#f44336' : '#42a5f5',
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Two-column body ── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          px: { xs: 2, md: 4 },
          py: 3,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '380px 1fr' },
          gap: 3,
          maxWidth: 1400,
          mx: 'auto',
          width: '100%',
        }}
      >
        {/* Left: Contracts */}
        <Box
          sx={{
            bgcolor: '#1a1a1a',
            borderRadius: 2.5,
            border: '1px solid rgba(255,255,255,0.07)',
            p: 2.5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ContractsPanel investmentId={Number(id)} />
        </Box>

        {/* Right: Payments */}
        <Box
          sx={{
            bgcolor: '#1a1a1a',
            borderRadius: 2.5,
            border: '1px solid rgba(255,255,255,0.07)',
            p: 2.5,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <PaymentsPanel investmentId={Number(id)} />
        </Box>
      </Box>
    </Box>
  );
}
